import memoize from 'lodash/memoize'
import sortBy from 'lodash/sortBy'
import { action, computed, flow, IObservableArray, makeObservable, observable, runInAction, toJS } from 'mobx'
import { IAnyModelType, types as t } from '@invisr/mobx-state-tree'
import { v4 as uuid } from 'uuid'
import delta from '../../utils/delta'
import errorToast from '../../utils/error-toast'
import fetch from '../../utils/fetch'
import squash from '../../utils/squash'
import upsertDataToArray from '../../utils/upsertDataToArray'
import DataTransformer from './filter'
import Mapping from './mapping'
import { getRecordClass } from './record/record'
import ref from './reference'
import Field from './field'
import DataSource from './datasource'
import type { FunctionLabel, LabelEntry } from './types'

export const databaseDatasourceTypes = {
  RAW_QUERY: {
    value: 'raw_query',
    label: 'Raw query'
  },
  QUERY: {
    value: 'query',
    label: 'Query'
  }
}

export const dbmsTypes = [
  { value: 'mssql', label: 'Microsoft SQL Server' },
//  { value: 'oracledb', label: 'Oracle' },
//  { value: 'mysql', label: 'MySQL' },
  { value: 'pg', label: 'Postgres' }
]

export const call = async (actionName, method, config, body = void 0) => {
  if (!config) return

  const res = await fetch(`/api/datasource/${config.dbKey}/${actionName}`, {
    method: method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      credentials: encodeURIComponent(config.credentials)
    },
    body: JSON.stringify(body)
  })

  let result
  try {
    result = await res.json()
  } catch (err) {
    result = await res.text()
  }

  if (!res.ok) {
    const error = result.error || result
    console.error(`Database Datasource error: ${config.name}`, error)
    throw error
  }

  return result
}

class TableMetadata {
  fields = []
  config

  async fetchFields () {
    const { dbms, credentials, schema, tableName } = this.config

    if (!this.fields.length && dbms && credentials && tableName) {
      try {
        const result = await call('getColumns/' + [schema, tableName].filter(x => x).join('/'), 'GET', this.config)
        runInAction(() => {
          this.fields = result.map(f => {
            return {
              fieldPath: f.name,
              label: f.name,
              type: f.type,
              tableName,
              schema,
              primaryKey: f.pk_index,
              autoincrement: f.autoincrement,
              nullable: f.nullable
            }
          })
        })
      } catch (er) {
        errorToast('Error getting table columns: ' + er)
      }
    }
    return this.fields
  }

  constructor (config, tableName) {
    makeObservable(this, {
      fields: observable,
      fetchFields: action,
      fieldList: computed,
      fieldMap: computed
    })

    this.config = config
    this.fetchFields()
  }

  get fieldList () {
    return this.fields
  }

  get fieldMap () {
    return this.fields && this.fields.reduce((acc, f) => { acc[f.name] = f; return acc }, {})
  }
}

export class DatabaseMetadata {
  tables = []
  table = null
  config = null
  credentials = null

  constructor (credentials, config) {
    this.config = config

    makeObservable(this, {
      tables: observable,
      table: observable
    })

    if (credentials && config.dbms) {
      this.credentials = credentials
      this.config = config

      try {
        call('getTables', 'GET', this.config).then(
          action(result => {
            this.tables = result.map(x => [x.schema, x.name].filter(x => x).join('.'))
          })
        )
      } catch (er) {
        errorToast('Error while fetching tables: ' + er)
      }
    }

    this.table = memoize((tableName) => {
      return new TableMetadata(config, tableName)
    })
  }
}

export class DatabaseProvider {
  data = [] as IObservableArray
  config = null
  fieldSet = [] as IObservableArray
  isLoading = false
  isLookupLoading = false
  lookupValues = []

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  setIsLookupLoading = () => {
    this.isLookupLoading = true
  }

  setIsLookupLoadingOff = () => {
    this.isLookupLoading = false
  }

  constructor (config = undefined, fieldSet = []) {
    makeObservable(this, {
      data: observable.shallow,
      isLoading: observable,
      isLookupLoading: observable,
      lookupValues: observable,
      setIsLoading: action,
      setIsLoadingOff: action,
      setIsLookupLoading: action,
      setIsLookupLoadingOff: action,
      onValueChange: action
    })

    this.config = config
    this.fieldSet = fieldSet as IObservableArray
  }

  onValueChange = (fieldpath, v) => {
    this.data[0][fieldpath] = v
  }

  /*
     PARAMS: _item, original
     OR
     PARAMS: _item: [ item, ... ]
     OR
     PARAMS: _item: [ [ item, original ], ... ]
   */
  upsert = flow(function * (this: DatabaseProvider, obj, originalObj) {
    if (!this.config.tableName) {
      errorToast('Table not selected.')
      return
    }

    const toUpdate = []
    const toInsert = []

    const pk = this.config.primaryKey

    // split into insert and update collection, do optimistic upserts
    const rows = upsertDataToArray(obj, originalObj)
    for (let { item, original = {} } of rows) {
      item[pk] = item[pk] || (original && original[pk])
      if (item[pk]) {
        toUpdate.push({ item, original, originalClone: { ...original }, referenceId: uuid() })
        // optimistic update
        const currentItemIndex = this.data.findIndex(d => d[pk] === item[pk])
        if (currentItemIndex >= 0 && this.data[currentItemIndex]) {
          this.data[currentItemIndex] = new this.config.Record(squash(item, original))
        }
      } else {
        const referenceId = uuid()
        toInsert.push({ item, original, originalClone: { ...original }, referenceId })
        // optimistic insert. give item temp id to track it
        this.data.push(new this.config.Record({ ...item, referenceId }))
      }
    }

    try {
      let insertResponse = []
      let updateResponse = []
      if (toInsert.length) {
        insertResponse = yield call('upsert?op=insert', 'POST', this.config, toInsert.map(x => {
          const item = { ...x.item }
          item[pk] = x.referenceId
          delete item.$
          return item
        }))
      }
      if (toUpdate.length) {
        updateResponse = yield call('upsert?op=update', 'POST', this.config, toUpdate.map(x => {
          const item = { ...x.item }
          delete item.referenceId
          delete item.$          
          return item
        }))
      }

      // handle inserts
      for (const [index, result] of insertResponse.entries()) {
        const referenceId = toInsert[index].referenceId
        const currentItemIndex = this.data.findIndex(d => d.referenceId === referenceId)
        const currentItem = this.data[currentItemIndex]

        if (result.error && currentItemIndex) {
          this.data.splice(currentItemIndex, 1)
          errorToast(`Insert failed: ${this.config.name}`, 'See the log for details')
          console.log(result.error)
        } else {
          currentItem.$.update({ [pk]: result })
        }
      }

      // handle updates
      for (const [index, result] of updateResponse.entries()) {
        const recordToUpdate = toUpdate[index]
        if (result.error) {
          errorToast(`Update failed ${this.config.name}`, 'See the log for details')
          console.log(result.error)
          const existingItem = this.data.find(r => r[pk] === recordToUpdate[pk])
          existingItem.$.update(recordToUpdate.originalClone)
        }
      }
      this.data.replace(this.data)
    } catch (e) {
      errorToast(`Error inserting ${this.config.name}`, 'See the log for details')
      console.log(e)
    }
  }).bind(this)

  lookup = async (searchValue = '', referenceData: Record<string, string> = {}) => {
    if (searchValue.length > 2) {
      this.setIsLookupLoading()
      const { label, value } = referenceData
      const referenceTo = this.config && this.config.tableName
      const result = await call(
        'lookup?search_value=' + encodeURIComponent(searchValue) + '&label=' + encodeURIComponent(label) + '&reference_to=' + encodeURIComponent(referenceTo),
        'GET',
        this.config
      )
      this.setIsLookupLoadingOff()
      return result
    }
  }

  remove = flow(function * (this: DatabaseProvider, items) {
    this.setIsLoading()
    try {

      const pk = this.config.primaryKey
      const rows = upsertDataToArray(items)
      const deleteResponse = yield call('remove', 'POST', this.config, rows.map(r => r.item[pk]))

      for (const result of deleteResponse) {
        if (result.error) {
          errorToast(`Remove failed: ${this.config.name}`, 'See the log for details')
          console.log(result.error)
        } else {
          const currentItemIndex = this.data.findIndex(row => row[pk] === result)
          if (currentItemIndex >= 0) {
            this.data.splice(currentItemIndex, 1)
          }
        }
      }
    } catch (err) {
      errorToast(`Remove error: ${this.config.name}`, 'See the log for details')
      console.log(err)
    }
    this.setIsLoadingOff()
  }).bind(this)

  query = async () => {
    this.setIsLoading()
    let results
    // RAW QUERY
    if (this.config.option === databaseDatasourceTypes.RAW_QUERY.value) {
      results = await this._rawQuery()
      // QUERY
    } else if (this.config.option === databaseDatasourceTypes.QUERY.value) {
      let where
      if (this.config.dataTransformer && this.config.dataTransformer.where) where = await this.config.dataTransformer.where.toSFasync()
      results = await this._transformerQuery({ where })
    }

    runInAction(() => {
      this.data = (Array.isArray(results) ? results.map(r => new this.config.Record(r)) : []) as IObservableArray
    })
    this.setIsLoadingOff()

    return this.data
  }

  serverQuery = async (opts: { where?: string[], count?: number} = {}) => {
    if (this.config.dataTransformer && this.config.dataTransformer.where) opts.where = [await this.config.dataTransformer.where.toSFasync(), ...Array.prototype.concat.call(opts.where || [])]
    const results = await this._transformerQuery(opts)
    return Array.isArray(results) && !opts.count ? results.map(r => new this.config.Record(r)) : results
  }

  _transformerQuery = async (opts) => {
    let results
    try {
      results = await call('query', 'POST', this.config, opts)
      return results
    } catch (err) {
      errorToast(`Error while querying ${this.config.name}`, 'See the log for details')
    }
  }

  _rawQuery = async () => {
    try {
      let results
      const rawQueryResolved = await this.config.mapping.mapper.convertProps()
      if (rawQueryResolved.query.length) {
        results = await call('rawquery?q=' + encodeURIComponent(rawQueryResolved.query), 'GET', this.config)

        if (results.errorCode) {
          throw Error(results.errorCode)
        }
      }
      return results
    } catch (e) {
      errorToast(`Error querying ${this.config.name}: `, e)
    }
  }
}

const optionValues = Object.values(databaseDatasourceTypes).map(i => i.value)

let metadata = null
export const DatabaseDataSource = t
  .model('Database', {
    type: t.optional(t.string, 'Database'),
    option: t.optional(t.enumeration('option', optionValues), optionValues[0]),
    _tableName: t.maybeNull(t.string),
    _fields: t.maybeNull(t.array(Field)),
    schema: t.maybeNull(t.string),
    primaryKey: t.maybeNull(t.string),
    // for raw queries
    mapping: t.maybeNull(t.late(() => Mapping)),
    dataTransformer: t.late(() => t.maybeNull(DataTransformer)),
    visibilityFilter: t.maybeNull(t.string),
    dbms: t.optional(t.enumeration('dbms', dbmsTypes.map(i => i.value)), dbmsTypes[0].value),
    queryFields: t.maybeNull(t.array(t.string)),
    parentDataSource: ref(t.late((): IAnyModelType => DataSource as any), 'dataSources')
  })
  .views(self => ({
    get metadata () {
      if (!metadata || metadata.credentials !== (self as any).credentials) metadata = new DatabaseMetadata((self as any).credentials, self)
      return metadata
    },
    getFields () {
      return (self as any).metadata && (self as any).metadata
        .table((self as any).tableName)
        .fieldList
    },
    get fields () {
      if (self.parentDataSource) return self.parentDataSource.fields
      return self._fields
    },    
    get provider () {
      return DatabaseProvider
    },
    get tableName () {
      if (self.parentDataSource) return self.parentDataSource.tableName
      return self._tableName
    },
    get fullTableName () {
      return [self.schema, (self as any).tableName].join('.')
    },
    get isValid () {
      return true
    }
  }))
  .views(self => ({
    get Record () {
      return getRecordClass({ recordType: (self as any).id, recordFields: self.fields })
    },
    fetchChildNodes () { 
      const fields = self.metadata.table(self.tableName).fields
      const childNodes = sortBy(
        fields.map(col => {
          const node = {
            label: col.fieldPath,
            secondaryLabel: col.type,
            meta: { ...toJS(col) }
          }
          return node
        }),
        c => c.label && c.label.toLowerCase()
      )
      
      return childNodes
    },
    fieldPath (obj): string | undefined {
      return obj?.meta?.fieldPath
    },
    labels () {
      const tempProvider = new DatabaseProvider()
      const functions = Object.keys(tempProvider)
        .filter(k => typeof tempProvider[k] === 'function')
        .filter(k => !k.startsWith('_'))
        .map((f): FunctionLabel => ({
          key: f,
          label: f,
          meta: { fieldPath: f },
          secondaryLabel: 'FUNCTION'
        }))
      functions.push({
        key: 'onValueChange',
        label: 'onValueChange',
        secondaryLabel: 'FUNCTION'
      })

      const labels: LabelEntry = {
        key: (self as any).id,
        label: (self as any).name,
        secondaryLabel: self.tableName,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: functions
          },
          {
            key: 'data',
            label: 'Data',
            meta: {
              table: self.tableName
            },
            asynch: true,
            childNodes: []
          },
          {
            key: 'state',
            label: 'State',
            childNodes: [
              {
                key: 'isLoading',
                label: 'isLoading',
                secondaryLabel: 'state'
              },
              {
                key: 'lookupValues',
                label: 'lookupValues',
                secondaryLabel: 'state'
              }
            ]
          }
        ]
      }
      return labels
    }
  }))
  .actions(self => ({
    update (key, val) {
      self[key] = val
    },
    updateQueryFields (fields) {
      self.queryFields = [...fields] as any
    },
    newTransformer () {
      self.dataTransformer = {} as any
    },
    updateItem (item) {
      (self as any).name = item.name || 'New DataSource'
      self._tableName = item._tableName
    },
    afterCreate () {
      if (!self.mapping) {
        (self as any).update('mapping', {
          type: 'object',
          children: [{ key: 'query', type: 'string', js: true }]
        })
      }
      if (!self.queryFields) self.queryFields = [] as any
    }
  }))
